import { Injectable } from "@angular/core";
import { ITemperatureAlarmRecord } from "@app/main/modules/temperature/_models/temperature/ITemperatureAlarmRecord";
import { ITemperatureOutOfBoundsHistory } from "@app/main/modules/temperature/_models/temperature/ITemperatureOutOfBoundsHistory";
import { ITemperatureProbe } from "@app/main/modules/temperature/_models/temperature/ITemperatureProbe";
import { ITemperatureProfile } from "@app/main/modules/temperature/_models/temperature/ITemperatureProfile";
import { TemperatureOutOfBoundsHistoryService } from "@app/main/modules/temperature/_services/temperature-out-of-bounds.service";
import { TemperatureProbeService } from "@app/main/modules/temperature/_services/temperature-probe.service";
import { TemperatureProfileService } from "@app/main/modules/temperature/_services/temperature-profile.service";
import { TemperatureRecordService } from "@app/main/modules/temperature/_services/temperature-record.service";
import { TranslationService } from "@app/services/translation.service";
import { UnitConversionService, UnitConversionType } from "@app/services/unitConversion.service";
import { combineLatest, lastValueFrom, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { IAlertRuleRecord } from "../../../_models/alerts.interfaces";
import { ITemperatureAlertDetailData } from "./ITemperatureAlertDetailData";

@Injectable({
    providedIn: "root"
})
export class TemperatureAlertDetailService {

    #alertDataCache: { [recordId: number]: ITemperatureAlertDetailData } = [];

    constructor(
        private readonly temperatureOutOfBoundsHistoryService: TemperatureOutOfBoundsHistoryService,
        private readonly temperatureProbeService: TemperatureProbeService,
        private readonly temperatureProfileService: TemperatureProfileService,
        private readonly temperatureRecordService: TemperatureRecordService,
        private readonly translationService: TranslationService,
        private readonly unitConversionService: UnitConversionService) { }

    getAlertData(record: IAlertRuleRecord): Observable<ITemperatureAlertDetailData> {
        if (this.#alertDataCache[record.Id]) {
            return of(this.#alertDataCache[record.Id]);
        }
        return combineLatest([
            this.temperatureProbeService.getTemperatureProbesForVehicle(record.VehicleId).pipe(map(probes => {
                return probes.find(probe => probe.Id === record.alertMetadata.TemperatureProbeId);
            })),
            this.temperatureOutOfBoundsHistoryService.getVehicleTemperatureOutOfBoundsHistory(record.alertMetadata.TemperatureOutOfBoundsHistoryId),
            this.temperatureProfileService.getTemperatureProfiles().pipe(map(profiles => {
                return profiles.find(profile => profile.Id === record.alertMetadata.TemperatureProfileId);
            })),
            this.temperatureRecordService.getTemperatureAlarmRecord(record.alertMetadata.TemperatureAlarmRecordId)
        ]).pipe(
            map(
                ([probe, outOfBoundsHistory, profile, alarmRecord]: [ITemperatureProbe, ITemperatureOutOfBoundsHistory, ITemperatureProfile, ITemperatureAlarmRecord]) => {
                    const alertDetailData: ITemperatureAlertDetailData = this.createAlertDetailData([probe, outOfBoundsHistory, profile, alarmRecord]);
                    alertDetailData.vehicle = record.vehicle;

                    this.#alertDataCache[record.Id] = alertDetailData;

                    return alertDetailData;
                })
        )

    }

    createAlertLabel(data: ITemperatureAlertDetailData): string {

        var convertTemperature = (value: number) => this.unitConversionService.smartConvert(value, UnitConversionType.Temperature);

        return `${convertTemperature(data.temperatureExtreme)} (${convertTemperature(data.lowerBound)} - ${convertTemperature(data.upperBound)})`
    }

    getAlertExport(record: IAlertRuleRecord): Promise<string> {
        return lastValueFrom(this.getAlertData(record)).then(data => {
            return `[${data.temperatureProbe?.Name}] ${this.createAlertLabel(data)}`;
        }).catch((error) => {
            console.error("Error retrieving temperature alert detail: ", error);
            return '';
        });
    }

    //--

    private createAlertDetailData(data: [ITemperatureProbe, ITemperatureOutOfBoundsHistory, ITemperatureProfile, ITemperatureAlarmRecord]): ITemperatureAlertDetailData {
        const probe: ITemperatureProbe = data[0];
        const outOfBoundsHistory: ITemperatureOutOfBoundsHistory = data[1];
        const profile: ITemperatureProfile = data[2];
        const alarmRecord: ITemperatureAlarmRecord = data[3];
   
        let detailData: ITemperatureAlertDetailData = {
            temperatureProfile: profile,
            temperatureOutOfBoundsHistory: outOfBoundsHistory,
            temperatureProbe: probe,
            temperatureAlarmRecord: alarmRecord,
            upperBound: profile?.UpperBound ?? alarmRecord?.UpperBound,
            lowerBound: profile?.LowerBound ?? alarmRecord?.LowerBound,
            temperatureExtreme: outOfBoundsHistory?.TemperatureExtreme ?? alarmRecord?.Temperature
        }

        if (detailData.temperatureOutOfBoundsHistory) {
            if (detailData.temperatureExtreme < detailData.lowerBound) {
                detailData.temperatureDiffExtreme = Math.abs(detailData.temperatureExtreme - detailData.lowerBound);
                detailData.temperatureDiffMessageExtreme = this.translationService.t('Alerts.BelowLowerBounds');
            } else {
                detailData.temperatureDiffExtreme = detailData.temperatureExtreme - detailData.upperBound;
                detailData.temperatureDiffMessageExtreme = this.translationService.t('Alerts.AboveUpperBounds');;
            }
        }

        return detailData;
    }
}
